import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import Card from '../components/Card'
import Button from '../components/Button'
import Tag from '../components/Tag'
import DateRangePicker from '../components/DateRangePicker'
import Modal from '../components/Modal'
import AIAssistantSettings from '../components/AIAssistantSettings'
import AIAssistantStep2 from '../components/AIAssistantStep2'
import AIAssistantResultCard from '../components/AIAssistantResultCard'
import ChartSettings from '../components/ChartSettings'
import DownloadSettings from '../components/DownloadSettings'
import ShareSettings from '../components/ShareSettings'
import ShareEmailModal from '../components/ShareEmailModal'
import TabBar from '../components/TabBar'
import iconDownload from '../assets/icons/download.svg'
import iconShare from '../assets/icons/share.svg'
import iconCog from '../assets/icons/settings.svg'
import iconTriangleUp from '../assets/icons/triangle-up.svg'
import iconTriangleDown from '../assets/icons/triangle-down.svg'
import iconAlert from '../assets/icons/alert.svg'
import iconAI from '../assets/icons/ai.svg'
import avatarBot from '../assets/icons/avatar.png'
import imgCapturasIcon from '../assets/capturas_icon.png'
import imgAutorizacionesIcon from '../assets/autorizaciones_icon.png'
import LoadingScreen from '../components/LoadingScreen'
// import N8nChat from '../components/N8nChat'
import { loadFilterSection } from '../utils/filterStorage'
import { getCurrentSeasonDateRange } from '../utils/seasonDateRange'
import './styles.css'

const AVISO_ICONS = {
  capturas: imgCapturasIcon,
  autorizaciones: imgAutorizacionesIcon,
}

const PRIORIDAD_TAG_VARIANT = {
  alta: 'error',
  media: 'warning',
  baja: 'info',
}

export default function PanelInteligente() {
  const navigate = useNavigate()
  const globalFilters = loadFilterSection('global') || {}

  // ? --- Tabs ---
  const [activeTab, setActiveTab] = useState('fechas')
  const tabs = [
    { id: 'fechas', label: 'Por fechas' },
    { id: 'especies', label: 'Por especies' },
    { id: 'temporadas', label: 'Por temporadas' },
    // { id: 'cotos', label: 'Por cotos' },
  ]

  // ? --- Filtro de fechas ---
  const [dateRange, setDateRange] = useState(
    globalFilters.dateRange || getCurrentSeasonDateRange()
  )

  // ? --- Datos del API ---
  const [resumen, setResumen] = useState(null)
  const [avisos, setAvisos] = useState([])
  const [anomalias, setAnomalias] = useState([])
  const [aiResult, setAiResult] = useState(null)
  const [anomaliasEspecies, setAnomaliasEspecies] = useState([])
  const [loadingEspecies, setLoadingEspecies] = useState(false)
  const [anomaliasCotos, setAnomaliasCotos] = useState([])
  const [loadingCotos, setLoadingCotos] = useState(false)
  const [umbralDiasInactivosCoto, setUmbralDiasInactivosCoto] = useState(5)
  const [umbralPicoCoto, setUmbralPicoCoto] = useState(100)
  const [loading, setLoading] = useState(true)

  // ? --- Modales ---
  const [isAIAssistantModalOpen, setIsAIAssistantModalOpen] = useState(false)
  const [isAIAssistantStep2ModalOpen, setIsAIAssistantStep2ModalOpen] = useState(false)
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false)
  const [isAnomaliaChartModalOpen, setIsAnomaliaChartModalOpen] = useState(false)
  const [isAnomaliaDownloadModalOpen, setIsAnomaliaDownloadModalOpen] = useState(false)
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isShareEmailModalOpen, setIsShareEmailModalOpen] = useState(false)

  // ? --- Configuración de modales ---
  const [aiDataType, setAiDataType] = useState('autorizaciones')
  const [aiPeriod, setAiPeriod] = useState({ startDate: '2025-01-15', endDate: '2025-01-15' })
  const [aiDataToExploit, setAiDataToExploit] = useState('usuarios-activos-vs-no-activos')
  const [aiChartType, setAiChartType] = useState('lineal')
  const [anomaliaChartType, setAnomaliaChartType] = useState('lineal')
  const [anomaliaFileType, setAnomaliaFileType] = useState('xls')
  const [selectedAnomaliaId, setSelectedAnomaliaId] = useState(null)
  const [fileType, setFileType] = useState('xls')
  const [shareType, setShareType] = useState('png')

  const shareEmailModalRef = useRef(null)
  const aiResultSectionRef = useRef(null)

  // ? --- Funciones auxiliares ---
  const formatDateForPicker = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const formatTimestamp = (isoString) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now - date
    const diffMinutes = Math.round(diffMs / 60000)
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    if (diffMinutes < 60) {
      return `(Actualizado hace ${diffMinutes} minutos, a las ${hours}:${minutes}h)`
    }
    const diffHours = Math.round(diffMinutes / 60)
    return `(Actualizado hace ${diffHours}h, a las ${hours}:${minutes}h)`
  }

  // ? --- Fetch de datos ---
  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params = { startDate: dateRange.startDate, endDate: dateRange.endDate }
      const [resumenRes, avisosRes, anomaliasRes] = await Promise.all([
        api.get('/panel-inteligente/resumen', { params }),
        api.get('/panel-inteligente/avisos', { params }),
        api.get('/panel-inteligente/anomalias', { params }),
      ])
      setResumen(resumenRes.data)
      setAvisos(avisosRes.data.avisos ?? [])
      setAnomalias(anomaliasRes.data.anomalias ?? [])
    } catch (error) {
      console.error('Error cargando datos del panel inteligente:', error)
    } finally {
      setLoading(false)
    }
  }, [dateRange.startDate, dateRange.endDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ? --- Fetch anomalías por especie ---
  const fetchAnomaliasEspecies = useCallback(async () => {
    setLoadingEspecies(true)
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }
      const res = await api.get('/panel-inteligente/anomalias-especies', { params })
      setAnomaliasEspecies(res.data.anomaliasEspecies ?? [])
    } catch (error) {
      console.error('Error cargando anomalías por especie:', error)
    } finally {
      setLoadingEspecies(false)
    }
  }, [dateRange.startDate, dateRange.endDate])

  useEffect(() => {
    if (activeTab === 'especies') {
      fetchAnomaliasEspecies()
    }
  }, [activeTab, fetchAnomaliasEspecies])

  // ? --- Fetch anomalías por coto ---
  const fetchAnomaliasCotos = useCallback(async () => {
    setLoadingCotos(true)
    try {
      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        umbralDiasInactivos: umbralDiasInactivosCoto,
        umbralPico: umbralPicoCoto,
      }
      const res = await api.get('/panel-inteligente/anomalias-cotos', { params })
      setAnomaliasCotos(res.data.anomaliasCotos ?? [])
    } catch (error) {
      console.error('Error cargando anomalías por coto:', error)
    } finally {
      setLoadingCotos(false)
    }
  }, [dateRange.startDate, dateRange.endDate, umbralDiasInactivosCoto, umbralPicoCoto])

  useEffect(() => {
    if (activeTab === 'cotos') {
      fetchAnomaliasCotos()
    }
  }, [activeTab, fetchAnomaliasCotos])

  // ? --- Acciones de descarga ---
  const handleDownload = async () => {
    try {
      const response = await api.get('/panel-inteligente/descargar', {
        params: { formato: fileType, startDate: dateRange.startDate, endDate: dateRange.endDate },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `panel_inteligente.${fileType === 'xls' ? 'xlsx' : fileType}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error al descargar:', error)
    }
    setIsDownloadModalOpen(false)
  }

  const handleAnomaliaDownload = async () => {
    if (!selectedAnomaliaId) return
    try {
      const response = await api.get(`/panel-inteligente/anomalias/${selectedAnomaliaId}/descargar`, {
        params: { formato: anomaliaFileType },
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `anomalia_${selectedAnomaliaId}.${anomaliaFileType === 'xls' ? 'xlsx' : anomaliaFileType}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error al descargar anomalía:', error)
    }
    setIsAnomaliaDownloadModalOpen(false)
  }

  // ? --- Acción de compartir ---
  const handleShare = async (email) => {
    try {
      await api.post('/panel-inteligente/compartir', {
        email,
        formato: shareType,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      })
    } catch (error) {
      console.error('Error al compartir:', error)
    }
    setIsShareEmailModalOpen(false)
  }

  // ? --- Asistente IA ---
  const handleAIAnalysis = async () => {
    setIsAIAssistantStep2ModalOpen(false)
    try {
      const response = await api.post('/panel-inteligente/asistente-ia/analizar', {
        tipoDatos: aiDataType,
        periodo: aiPeriod,
        datosExplotar: aiDataToExploit,
        tipoGrafico: aiChartType,
      })
      setAiResult(response.data)
      setTimeout(() => {
        if (aiResultSectionRef.current) {
          aiResultSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    } catch (error) {
      console.error('Error al analizar con IA:', error)
    }
  }

  const handleDeleteAIResult = async () => {
    if (aiResult?.id) {
      try {
        await api.delete(`/panel-inteligente/asistente-ia/${aiResult.id}`)
      } catch (error) {
        console.error('Error al eliminar análisis IA:', error)
      }
    }
    setAiResult(null)
    setIsDeleteConfirmModalOpen(false)
  }

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <div className="page-general">
      <div className="panel-header">
        <h1 className="page-title">Panel Inteligente</h1>
        <div className="panel-timestamp">
          {resumen ? formatTimestamp(resumen.ultimaActualizacion) : ''}
        </div>
      </div>

      <div className="page-tabs">
        <TabBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* //!!!!!! ===== TAB: Por fechas ===== */}
      {activeTab === 'fechas' && (
        <>
          {/* Banner de alerta */}
          {/* {resumen && (resumen.totalAvisos > 0 || resumen.totalAnomalias > 0) && (
            <div className="panel-alert-banner">
              <div className="alert-icon-circle">
                <img src={iconAlert} alt="" className="alert-icon" />
              </div>
              <div className="alert-text">
                <span className="alert-text-primary">Se ha detectado una situación anómala que requiere tu atención:</span>
                <span className="alert-text-secondary"> Tienes {resumen.totalAvisos} avisos y {resumen.totalAnomalias} anomalías detectadas.</span>
              </div>
            </div>
          )} */}

          {/* Contenedor principal */}
          <div className="panel-main-container">
            <div className="panel-filters-section-container">
              {/* Filtros y acciones */}
              <div className="page-filters">
                <div className="filter-group">
                  <label className="filter-label">Seleccione un rango de fechas</label>
                  <DateRangePicker
                    startDate={formatDateForPicker(dateRange.startDate)}
                    endDate={formatDateForPicker(dateRange.endDate)}
                    onChange={setDateRange}
                  />
                </div>
                <div className="filter-actions">
                  <Button variant="accent" onClick={() => setIsDownloadModalOpen(true)}>
                    <img src={iconDownload} alt="" className="button-icon button-icon-white" />
                    <span>Descargar</span>
                  </Button>
                  {/*
                  <Button variant="primary" onClick={() => setIsAIAssistantModalOpen(true)}>
                    <span>Asistente IA</span>
                    <img src={iconAI} alt="" />
                  </Button>
                  */}
                  {/* <Button variant="primary" onClick={() => setIsShareModalOpen(true)}>
                    <img src={iconShare} alt="" />
                  </Button> */}
                </div>
              </div>

              {/* Sección Avisos */}
              {avisos.length > 0 && (
              <div className="panel-section">
                <h2 className="panel-section-title">Avisos({avisos.length})</h2>
                <div className="avisos-grid">
                  {avisos.map((aviso) => (
                    <Card key={aviso.id} className="aviso-card">
                      <div className="aviso-card-content">
                        <img src={AVISO_ICONS[aviso.tipo] || imgCapturasIcon} alt="" className="aviso-icon" />
                        <div className="aviso-info">
                          <div className="aviso-header-container">
                            <div className="aviso-header">
                              <div className="aviso-label">{aviso.label}</div>
                              <div className="aviso-date-tag">Hoy: {aviso.fecha}</div>
                            </div>
                            <div className="aviso-value">{aviso.valor.toLocaleString('es-ES')}</div>
                          </div>
                          <div className={`aviso-trend aviso-trend--${aviso.tendencia.direccion}`}>
                            <img
                              src={aviso.tendencia.direccion === 'up' ? iconTriangleUp : iconTriangleDown}
                              alt=""
                              className="trend-icon"
                            />
                            <span className="trend-number">{aviso.tendencia.cantidad}</span>
                            <span className="trend-text">{aviso.tendencia.texto}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
              )}
            </div>

            {/* Sección Anomalías */}
            {anomalias.length > 0 && (
            <div className="panel-section">
              <h2 className="panel-section-title">Anomalías detectadas({anomalias.length})</h2>

              <div className="anomalias-cards-row">
                {anomalias.map((anomalia) => (
                  <Card key={anomalia.id} className="anomalia-card">
                    <div className="anomalia-content">
                      <div className="anomalia-header">
                        <div className="anomalia-title-section">
                          <h3 className="card-title">{anomalia.titulo}</h3>
                          <div className={`anomalia-change anomalia-change--${anomalia.cambio?.direccion}`}>
                            <img
                              src={anomalia.cambio?.direccion === 'up' ? iconTriangleUp : iconTriangleDown}
                              alt=""
                              className="trend-icon"
                            />
                            <span className="trend-number">{anomalia.cambio?.cantidad?.toLocaleString('es-ES')}</span>
                            <span className="trend-text">{anomalia.cambio?.texto}</span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <Tag variant={anomalia.tipo === 'pico' ? 'warning' : 'info'}>
                            {anomalia.tipo === 'pico' ? 'Por encima del límite' : 'Por debajo del límite'}
                          </Tag>
                          <Tag variant={PRIORIDAD_TAG_VARIANT[anomalia.prioridad] || 'default'}>
                            Prioridad: {anomalia.prioridad.charAt(0).toUpperCase() + anomalia.prioridad.slice(1)}
                          </Tag>
                        </div>
                      </div>
                      <div className="anomalia-description-details">
                        <p className="anomalia-description">
                          {anomalia.descripcion}
                        </p>
                        <div className="anomalia-details">
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Semana:</span>
                            <span className="detail-value">{anomalia.semana}</span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Valor registrado:</span>
                            <span className="detail-value" style={{ fontWeight: '700', color: anomalia.tipo === 'pico' ? '#DF7C13' : '#3B82F6' }}>
                              {anomalia.valor?.toLocaleString('es-ES')}
                            </span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Límite {anomalia.tipo === 'pico' ? 'superior' : 'inferior'} esperado:</span>
                            <span className="detail-value">
                              {anomalia.tipo === 'pico'
                                ? anomalia.limiteSuperior?.toLocaleString('es-ES')
                                : anomalia.limiteInferior?.toLocaleString('es-ES')
                              }
                            </span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Diferencia:</span>
                            <span className="detail-value" style={{ fontWeight: '600', color: anomalia.tipo === 'pico' ? '#DF7C13' : '#3B82F6' }}>
                              {anomalia.tipo === 'pico'
                                ? `+${(anomalia.valor - anomalia.limiteSuperior)?.toLocaleString('es-ES')} por encima`
                                : `${(anomalia.valor - anomalia.limiteInferior)?.toLocaleString('es-ES')} por debajo`
                              }
                            </span>
                          </div>
                        </div>

                        {/* Especies afectadas (solo en capturas) */}
                        {anomalia.especiesAfectadas && anomalia.especiesAfectadas.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <span className="detail-label" style={{ display: 'block', marginBottom: '6px' }}>Especies afectadas:</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {anomalia.especiesAfectadas.map((especie) => (
                                <div key={especie.especie_id} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Open Sans', fontSize: '13px' }}>
                                  <span style={{ color: '#475958' }}>{especie.especie_nombre}</span>
                                  <span style={{ color: '#064340', fontWeight: '600' }}>{especie.capturas?.toLocaleString('es-ES')} capturas</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
            )}

            {/* Sección Herramienta Asistente de IA */}
            {aiResult && (
              <div className="panel-section" ref={aiResultSectionRef}>
                <h2 className="panel-section-title">Herramienta Asistente de IA</h2>
                <AIAssistantResultCard
                  title={aiResult.titulo}
                  change={{
                    value: aiResult.cambio.valor,
                    type: aiResult.cambio.direccion,
                    text: aiResult.cambio.texto
                  }}
                  insights={{
                    main: aiResult.insights.principal,
                    metrics: aiResult.insights.metricas.map(m => ({ label: m.label, value: m.valor }))
                  }}
                  period={aiResult.periodo}
                  navigateTo={aiResult.enlace}
                  onSettingsClick={() => setIsAIAssistantStep2ModalOpen(true)}
                  onDeleteClick={() => setIsDeleteConfirmModalOpen(true)}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* //!!!!!! ===== TAB: Por temporadas ===== */}
      {activeTab === 'temporadas' && (
        <div className="panel-main-container">
          <div className="panel-section" style={{ textAlign: 'center', padding: '80px 20px' }}>
            <h2 className="panel-section-title" style={{ fontSize: '24px', color: '#475958' }}>Próximamente</h2>
            <p style={{ fontFamily: 'Open Sans', fontSize: '14px', color: '#6B7B7A', marginTop: '8px' }}>
              Esta sección estará disponible en una futura actualización.
            </p>
          </div>
        </div>
      )}

      {/* //!!!!!! ===== TAB: Por especies ===== */}
      {activeTab === 'especies' && (
        <div className="panel-main-container">
          {/* Filtros y acciones */}
          <div className="page-filters">
            <div className="filter-group">
              <label className="filter-label">Seleccione un rango de fechas</label>
              <DateRangePicker
                startDate={formatDateForPicker(dateRange.startDate)}
                endDate={formatDateForPicker(dateRange.endDate)}
                onChange={setDateRange}
              />
            </div>
            <div className="filter-actions">
              <Button variant="accent" onClick={() => setIsDownloadModalOpen(true)}>
                <img src={iconDownload} alt="" className="button-icon button-icon-white" />
                <span>Descargar</span>
              </Button>
            </div>
          </div>

          {/* Resumen */}
          <div className="panel-section">
            <h2 className="panel-section-title">Resumen por especies</h2>
            <div className="avisos-grid">
              <Card className="aviso-card">
                <div className="aviso-card-content">
                  <div className="aviso-info">
                    <div className="aviso-header-container">
                      <div className="aviso-header">
                        <div className="aviso-label">Total de especies registradas</div>
                      </div>
                      <div className="aviso-value">{resumen?.totalEspecies ?? '—'}</div>
                    </div>
                  </div>
                </div>
              </Card>
              <Card className="aviso-card">
                <div className="aviso-card-content">
                  <div className="aviso-info">
                    <div className="aviso-header-container">
                      <div className="aviso-header">
                        <div className="aviso-label">Especies con anomalías</div>
                      </div>
                      <div className="aviso-value">
                        {loadingEspecies ? '...' : new Set(anomaliasEspecies.map(a => a.especie_id)).size}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Anomalías por especie — Rango Intercuartílico */}
          <div className="panel-section">
            <h2 className="panel-section-title">Anomalías detectadas por especie — Rango Intercuartílico ({anomaliasEspecies.length})</h2>
            {loadingEspecies ? (
              <p style={{ fontFamily: 'Open Sans', fontSize: '14px', color: '#6B7B7A' }}>Cargando anomalías por especie...</p>
            ) : anomaliasEspecies.length === 0 ? (
              <p style={{ fontFamily: 'Open Sans', fontSize: '14px', color: '#6B7B7A' }}>No se han detectado anomalías por especie en el rango seleccionado.</p>
            ) : (
              <div className="anomalias-cards-row">
                {anomaliasEspecies.map((item) => (
                  <Card key={item.id ?? item.especie_id} className="anomalia-card">
                    <div className="anomalia-content">
                      <div className="anomalia-header">
                        <div className="anomalia-title-section">
                          <h3 className="card-title">{item.especie_nombre}</h3>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                          <Tag variant={item.tipo === 'pico' ? 'warning' : 'info'}>
                            {item.tipo === 'pico' ? 'Por encima del límite' : 'Por debajo del límite'}
                          </Tag>
                          <Tag variant={PRIORIDAD_TAG_VARIANT[item.prioridad] || 'default'}>
                            Prioridad: {item.prioridad.charAt(0).toUpperCase() + item.prioridad.slice(1)}
                          </Tag>
                        </div>
                      </div>
                      <div className="anomalia-description-details">
                        <div className="anomalia-details">
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Semana:</span>
                            <span className="detail-value">{item.semana}</span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Capturas en la semana:</span>
                            <span className="detail-value" style={{ fontWeight: '700', color: item.tipo === 'pico' ? '#DF7C13' : '#3B82F6' }}>
                              {item.capturasSemana?.toLocaleString('es-ES')}
                            </span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Límite {item.tipo === 'pico' ? 'superior' : 'inferior'} esperado:</span>
                            <span className="detail-value">
                              {item.tipo === 'pico'
                                ? item.limiteSuperior?.toLocaleString('es-ES')
                                : item.limiteInferior?.toLocaleString('es-ES')
                              }
                            </span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Diferencia:</span>
                            <span className="detail-value" style={{ fontWeight: '600', color: item.tipo === 'pico' ? '#DF7C13' : '#3B82F6' }}>
                              {item.tipo === 'pico'
                                ? `+${(item.capturasSemana - item.limiteSuperior)?.toLocaleString('es-ES')} capturas por encima`
                                : `${(item.capturasSemana - item.limiteInferior)?.toLocaleString('es-ES')} capturas por debajo`
                              }
                            </span>
                          </div>
                        </div>

                        {/* Posibles causas */}
                        {item.posiblesCausas && item.posiblesCausas.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <span className="detail-label" style={{ display: 'block', marginBottom: '4px' }}>Posibles causas:</span>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {item.posiblesCausas.map((causa, i) => (
                                <Tag key={i} variant="warning">{causa}</Tag>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Cotos afectados */}
                        {item.cotosAfectados && item.cotosAfectados.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <span className="detail-label" style={{ display: 'block', marginBottom: '6px' }}>Cotos afectados:</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {item.cotosAfectados.map((coto) => (
                                <div key={coto.coto_id} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Open Sans', fontSize: '13px' }}>
                                  <span style={{ color: '#475958' }}>{coto.coto_nombre}</span>
                                  <span style={{ color: '#064340', fontWeight: '600' }}>{coto.capturas?.toLocaleString('es-ES')} capturas</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* //!!!!!! ===== TAB: Por cotos ===== */}
      {activeTab === 'cotos' && (
        <div className="panel-main-container">
          {/* Filtros y acciones */}
          <div className="page-filters">
            <div className="filter-group">
              <label className="filter-label">Seleccione un rango de fechas</label>
              <DateRangePicker
                startDate={formatDateForPicker(dateRange.startDate)}
                endDate={formatDateForPicker(dateRange.endDate)}
                onChange={setDateRange}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Días inactivos mínimos</label>
              <input
                type="number"
                min="1"
                value={umbralDiasInactivosCoto}
                onChange={(e) => setUmbralDiasInactivosCoto(Math.max(1, parseInt(e.target.value) || 1))}
                className="text-field"
                style={{ width: '120px' }}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Capturas mínimas para pico</label>
              <input
                type="number"
                min="1"
                value={umbralPicoCoto}
                onChange={(e) => setUmbralPicoCoto(Math.max(1, parseInt(e.target.value) || 1))}
                className="text-field"
                style={{ width: '120px' }}
              />
            </div>
            <div className="filter-actions">
              <Button variant="accent" onClick={() => setIsDownloadModalOpen(true)}>
                <img src={iconDownload} alt="" className="button-icon button-icon-white" />
                <span>Descargar</span>
              </Button>
            </div>
          </div>

          {/* Resumen */}
          <div className="panel-section">
            <h2 className="panel-section-title">Resumen por cotos</h2>
            <div className="avisos-grid">
              <Card className="aviso-card">
                <div className="aviso-card-content">
                  <div className="aviso-info">
                    <div className="aviso-header-container">
                      <div className="aviso-header">
                        <div className="aviso-label">Total de cotos gestionados</div>
                      </div>
                      <div className="aviso-value">{resumen?.totalCotos ?? '—'}</div>
                    </div>
                  </div>
                </div>
              </Card>
              <Card className="aviso-card">
                <div className="aviso-card-content">
                  <div className="aviso-info">
                    <div className="aviso-header-container">
                      <div className="aviso-header">
                        <div className="aviso-label">Cotos con incidencias</div>
                      </div>
                      <div className="aviso-value">
                        {loadingCotos ? '...' : new Set(anomaliasCotos.map(a => a.coto_id)).size}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Anomalías por coto — Picos tras inactividad */}
          <div className="panel-section">
            <h2 className="panel-section-title">Anomalías detectadas por coto — Picos tras inactividad ({anomaliasCotos.length})</h2>
            {loadingCotos ? (
              <p style={{ fontFamily: 'Open Sans', fontSize: '14px', color: '#6B7B7A' }}>Cargando anomalías por coto...</p>
            ) : anomaliasCotos.length === 0 ? (
              <p style={{ fontFamily: 'Open Sans', fontSize: '14px', color: '#6B7B7A' }}>No se han detectado anomalías por coto en el rango seleccionado.</p>
            ) : (
              <div className="anomalias-cards-row">
                {anomaliasCotos.map((item) => (
                  <Card key={item.id ?? item.coto_id} className="anomalia-card">
                    <div className="anomalia-content">
                      <div className="anomalia-header">
                        <div className="anomalia-title-section">
                          <h3 className="card-title">{item.coto_nombre}</h3>
                          <div className="anomalia-deviation">
                            <span className="trend-number" style={{ color: '#DF7C13' }}>{item.desviacion}</span>
                            <span className="trend-text"> desviación sobre la media</span>
                          </div>
                        </div>
                        <Tag variant={PRIORIDAD_TAG_VARIANT[item.prioridad] || 'default'}>
                          Prioridad: {item.prioridad.charAt(0).toUpperCase() + item.prioridad.slice(1)}
                        </Tag>
                      </div>
                      <div className="anomalia-description-details">
                        <p className="anomalia-description">
                          {item.descripcion}
                        </p>
                        <div className="anomalia-details">
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Días inactivos:</span>
                            <span className="detail-value">{item.diasInactivos}</span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Última captura antes del pico:</span>
                            <span className="detail-value">{item.fechaUltimaCaptura}</span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Fecha del pico:</span>
                            <span className="detail-value">{item.fechaPico}</span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Capturas en el pico:</span>
                            <span className="detail-value">{item.capturasPico?.toLocaleString('es-ES')}</span>
                          </div>
                          <div className="anomalia-detail-item">
                            <span className="detail-label">Media histórica diaria:</span>
                            <span className="detail-value">{item.mediaHistorica?.toLocaleString('es-ES')}</span>
                          </div>
                        </div>

                        {/* Posibles causas */}
                        {item.posiblesCausas && item.posiblesCausas.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <span className="detail-label" style={{ display: 'block', marginBottom: '4px' }}>Posibles causas:</span>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {item.posiblesCausas.map((causa, i) => (
                                <Tag key={i} variant="warning">{causa}</Tag>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Especies afectadas */}
                        {item.especiesAfectadas && item.especiesAfectadas.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <span className="detail-label" style={{ display: 'block', marginBottom: '6px' }}>Especies afectadas:</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {item.especiesAfectadas.map((especie) => (
                                <div key={especie.especie_id} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Open Sans', fontSize: '13px' }}>
                                  <span style={{ color: '#475958' }}>{especie.especie_nombre}</span>
                                  <span style={{ color: '#064340', fontWeight: '600' }}>{especie.capturas?.toLocaleString('es-ES')} capturas</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={isAIAssistantModalOpen}
        onClose={() => setIsAIAssistantModalOpen(false)}
        onApply={() => {
          setIsAIAssistantModalOpen(false)
          setIsAIAssistantStep2ModalOpen(true)
        }}
        title="Asistente de IA"
        applyLabel="Continuar"
        cancelLabel="Cerrar"
      >
        <AIAssistantSettings
          dataType={aiDataType}
          onDataTypeChange={setAiDataType}
        />
      </Modal>

      <Modal
        isOpen={isAIAssistantStep2ModalOpen}
        onClose={() => setIsAIAssistantStep2ModalOpen(false)}
        onBack={() => {
          setIsAIAssistantStep2ModalOpen(false)
          setIsAIAssistantModalOpen(true)
        }}
        onApply={handleAIAnalysis}
        title="Asistente de IA"
        applyLabel="Aplicar"
        cancelLabel="Cerrar"
        backLabel="← Volver"
      >
        <AIAssistantStep2
          periodStartDate={aiPeriod.startDate}
          periodEndDate={aiPeriod.endDate}
          onPeriodChange={setAiPeriod}
          dataToExploit={aiDataToExploit}
          onDataToExploitChange={setAiDataToExploit}
          chartType={aiChartType}
          onChartTypeChange={setAiChartType}
        />
      </Modal>

      <Modal
        isOpen={isDeleteConfirmModalOpen}
        onClose={() => setIsDeleteConfirmModalOpen(false)}
        onApply={handleDeleteAIResult}
        title="Eliminar"
        applyLabel="Eliminar"
        cancelLabel="Cancelar"
      >
        <p style={{ margin: 0, fontFamily: 'Open Sans', fontSize: '14px', color: '#475958', lineHeight: '20px' }}>
          ¿Estás seguro que quieres eliminar esta tarjeta?
        </p>
      </Modal>

      <Modal
        isOpen={isAnomaliaChartModalOpen}
        onClose={() => setIsAnomaliaChartModalOpen(false)}
        onApply={() => {
          setIsAnomaliaChartModalOpen(false)
        }}
      >
        <ChartSettings
          chartType={anomaliaChartType}
          onChartTypeChange={setAnomaliaChartType}
        />
      </Modal>

      <Modal
        isOpen={isAnomaliaDownloadModalOpen}
        onClose={() => setIsAnomaliaDownloadModalOpen(false)}
        onApply={handleAnomaliaDownload}
        title="Descargar"
      >
        <DownloadSettings
          fileType={anomaliaFileType}
          onFileTypeChange={setAnomaliaFileType}
        />
      </Modal>

      <Modal
        isOpen={isDownloadModalOpen}
        onClose={() => setIsDownloadModalOpen(false)}
        onApply={handleDownload}
        title="Descargar"
      >
        <DownloadSettings
          fileType={fileType}
          onFileTypeChange={setFileType}
        />
      </Modal>

      <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onApply={() => {
          setIsShareModalOpen(false)
          setIsShareEmailModalOpen(true)
        }}
        title="Compartir"
      >
        <ShareSettings
          shareType={shareType}
          onShareTypeChange={setShareType}
        />
      </Modal>

      <Modal
        isOpen={isShareEmailModalOpen}
        onClose={() => setIsShareEmailModalOpen(false)}
        onApply={() => {
          if (shareEmailModalRef.current) {
            shareEmailModalRef.current.send()
          }
        }}
        title="Compartir"
        applyLabel="Enviar"
        cancelLabel="Cancelar"
      >
        <ShareEmailModal
          ref={shareEmailModalRef}
          onSend={(email) => handleShare(email)}
          onCancel={() => setIsShareEmailModalOpen(false)}
        />
      </Modal>

      {/* <N8nChat /> */}
    </div>
  )
}


